import { AlertCircle, Check, CircleStop, Info, Play, Rocket } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { WorkspaceProgressStep } from '@/lib/types/rome';
import { cn } from '@/lib/utils/cn';

// Steps that show the GPU allocation info tooltip
const GPU_ALLOCATION_STEPS = new Set(['scheduling']);

// Lifecycle step keys that aren't part of startup provisioning
const LIFECYCLE_STEPS = new Set(['start_requested', 'running', 'stopping', 'stopped', 'errored']);

interface StartupTimelineProps {
  steps: WorkspaceProgressStep[];
  showFooter?: boolean;
  className?: string;
}

/**
 * Formats a unix timestamp into a relative time string (e.g. "2s", "1m 30s").
 */
export function formatRelativeTime(startTimestamp: number, endTimestamp: number): string {
  const diffSeconds = Math.max(0, endTimestamp - startTimestamp);
  if (diffSeconds < 60) return `${diffSeconds}s`;
  const minutes = Math.floor(diffSeconds / 60);
  const seconds = diffSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/**
 * Formats a unix timestamp to a date+time string (e.g. "Feb 13 at 2:34 PM" or "Today at 2:34 PM").
 */
export function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `Today at ${time}`;
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`;
}

/**
 * Get the icon and color for a step based on its key, status, and position.
 *
 * @param step - The progress step.
 * @param isLast - Whether this is the last step in the timeline.
 */
function getStepIcon(step: WorkspaceProgressStep, isLast: boolean) {
  // Lifecycle events get special icons
  if (step.step === 'start_requested') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-blue-500/15">
        <Rocket className="h-3 w-3 text-blue-500" />
      </div>
    );
  }
  if (step.step === 'running') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-green-500/15">
        <Play className="h-3 w-3 text-green-500" />
      </div>
    );
  }
  if (step.step === 'stopped') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-yellow-500/15">
        <CircleStop className="h-3 w-3 text-yellow-500" />
      </div>
    );
  }
  if (step.step === 'errored') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-red-500/15">
        <AlertCircle className="h-3 w-3 text-red-500" />
      </div>
    );
  }
  if (step.step === 'stopping') {
    // Only animate when stopping is the most recent step (still in progress)
    if (isLast && step.status === 'in_progress') {
      return (
        <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-orange-500/15">
          <Spinner size="h-3 w-3" className="text-orange-500" />
        </div>
      );
    }
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-orange-500/15">
        <Check className="h-3 w-3 text-orange-500" />
      </div>
    );
  }

  // Standard status icons
  if (step.status === 'completed') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-green-500/15">
        <Check className="h-3 w-3 text-green-500" />
      </div>
    );
  }
  if (step.status === 'in_progress') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-blue-500/15">
        <Spinner size="h-3 w-3" className="text-blue-500" />
      </div>
    );
  }
  if (step.status === 'failed') {
    return (
      <div className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-red-500/15">
        <AlertCircle className="h-3 w-3 text-red-500" />
      </div>
    );
  }

  return null;
}

/**
 * Get the connector line color between steps.
 */
function getConnectorColor(step: WorkspaceProgressStep, nextStep: WorkspaceProgressStep | undefined) {
  // Blue connector from start_requested into the provisioning sequence
  if (step.step === 'start_requested') return 'bg-blue-500/30';
  // Red connector into errored step
  if (nextStep?.step === 'errored') return 'bg-red-500/30';
  // Yellow connectors for the shutdown sequence (running → stopping → stopped)
  if (step.step === 'running' || step.step === 'stopping') {
    return 'bg-yellow-500/30';
  }
  if (step.status === 'completed') return 'bg-green-500/40';
  return 'bg-border';
}

/**
 * Compact vertical timeline showing workspace startup progress steps.
 *
 * Renders each step with an icon indicating status (completed, in_progress,
 * or failed), the step message, and timing information. Lifecycle events
 * (running, stopped) are rendered with distinct icons. Shows total startup
 * duration when all provisioning steps are completed.
 */
export function StartupTimeline({ steps, showFooter = true, className }: StartupTimelineProps) {
  if (steps.length === 0) {
    return <p className={cn('text-muted-foreground text-xs italic', className)}>No lifecycle history available</p>;
  }

  // Synthesize a "Start requested" step at the beginning using the first step's timestamp
  const firstTimestamp = steps[0]?.timestamp ?? 0;
  const startRequestedStep: WorkspaceProgressStep = {
    step: 'start_requested',
    message: 'Start requested',
    timestamp: firstTimestamp,
    status: 'completed',
  };
  const allSteps = [startRequestedStep, ...steps];
  const runningStep = allSteps.find((s) => s.step === 'running');
  const startupDuration = runningStep ? formatRelativeTime(firstTimestamp, runningStep.timestamp) : null;

  // Check if startup had a failure
  const hasFailed = allSteps.some((s) => s.status === 'failed');

  return (
    <div className={cn('space-y-0', className)}>
      <div className="relative">
        {allSteps.map((step, index) => {
          const isLast = index === allSteps.length - 1;
          const nextStep = !isLast ? allSteps[index + 1] : undefined;
          const isLifecycle = LIFECYCLE_STEPS.has(step.step);

          // Show how long this step took (completed: until next step, in_progress: until now)
          let duration: string | null = null;
          if (step.step === 'start_requested') {
            // No duration for the initial trigger event
          } else if (step.status === 'completed' && nextStep) {
            duration = formatRelativeTime(step.timestamp, nextStep.timestamp);
          } else if (step.status === 'in_progress') {
            duration = formatRelativeTime(step.timestamp, Math.floor(Date.now() / 1000));
          }

          return (
            <div key={step.step} className="relative flex items-start gap-3 pb-5 last:pb-0">
              {/* Vertical connector line — starts flush below current circle */}
              {!isLast && (
                <div
                  className={cn(
                    'absolute top-[22px] bottom-[2px] left-[9px] w-[2px]',
                    getConnectorColor(step, nextStep)
                  )}
                />
              )}

              {/* Status icon */}
              <div className="relative z-10 flex-shrink-0">{getStepIcon(step, isLast)}</div>

              {/* Step content */}
              <div className="min-w-0 flex-1 pt-px">
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'font-medium text-xs leading-4',
                      isLifecycle && step.step === 'start_requested' && 'text-blue-600 dark:text-blue-400',
                      isLifecycle && step.step === 'running' && 'text-green-600 dark:text-green-400',
                      isLifecycle && step.step === 'stopped' && 'text-yellow-600 dark:text-yellow-400',
                      isLifecycle && step.step === 'stopping' && 'text-orange-600 dark:text-orange-400',
                      isLifecycle && step.step === 'errored' && 'text-red-600 dark:text-red-400',
                      !isLifecycle && step.status === 'completed' && 'text-foreground',
                      !isLifecycle && step.status === 'in_progress' && 'text-blue-500',
                      !isLifecycle && step.status === 'failed' && 'text-red-500'
                    )}
                  >
                    {step.message}
                  </span>
                  {GPU_ALLOCATION_STEPS.has(step.step) && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex flex-shrink-0 translate-y-px">
                            <Info className="h-3 w-3 cursor-help text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="z-[1200] max-w-[220px]">
                          <p className="text-xs leading-relaxed">
                            This can sometimes take awhile in periods of high demand, or when you haven't used Antioch
                            recently. Waiting up to 5 minutes for a GPU is not unusual. If delays persist, please let us
                            know using the support function.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <span className="mt-0.5 block text-[10px] text-muted-foreground leading-none">
                  {formatTime(step.timestamp)}
                  {duration && (
                    <>
                      <span className="mx-1">·</span>
                      <span className="tabular-nums">{duration}</span>
                    </>
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total startup duration footer */}
      {showFooter && startupDuration && !hasFailed && (
        <div className="mt-2 border-border/50 border-t pt-2">
          <span className="text-[10px] text-muted-foreground">Total startup time: {startupDuration}</span>
        </div>
      )}
    </div>
  );
}
